import React from 'react';
import { Network, Download, Upload, Signal } from 'lucide-react';

/**
 * Summary traffic WAN (Download/Upload/Total + ratio bar).
 * Auto-pilih interface dengan comment/name "WAN" atau ether1.
 */
export const MPanelWANTraffic = ({ visible, interfaces, formatBytes }) => {
  if (!visible) return null;
  if (!interfaces || interfaces.length === 0) return null;

  const wanInterface = interfaces.find(
    (iface) =>
      (iface.comment && iface.comment.toLowerCase().includes('wan')) ||
      iface.name.toLowerCase().includes('wan') ||
      iface.name === 'ether1'
  );

  const totalRx = interfaces.reduce((s, i) => s + (i.rx_byte || 0), 0);
  const totalTx = interfaces.reduce((s, i) => s + (i.tx_byte || 0), 0);
  const rxBytes = wanInterface ? wanInterface.rx_byte : totalRx;
  const txBytes = wanInterface ? wanInterface.tx_byte : totalTx;
  const interfaceName = wanInterface ? wanInterface.name : 'All Interfaces';
  const totalBytes = rxBytes + txBytes;
  const rxPct = totalBytes > 0 ? (rxBytes / totalBytes) * 100 : 0;
  const txPct = totalBytes > 0 ? (txBytes / totalBytes) * 100 : 0;

  return (
    <div
      data-testid="mpanel-wan-traffic"
      className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <Network className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">WAN Traffic Summary</h2>
        <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full font-medium">
          {interfaceName}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-2">
            <Download className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Download (RX)</span>
          </div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{formatBytes(rxBytes)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total received</div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Upload (TX)</span>
          </div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{formatBytes(txBytes)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total transmitted</div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-2">
            <Signal className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Traffic</span>
          </div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{formatBytes(totalBytes)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">RX + TX</div>
        </div>
      </div>

      <div className="mt-4 bg-white dark:bg-slate-800 rounded-lg p-3 border border-blue-100 dark:border-blue-800">
        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-2">
          <span>Download vs Upload Ratio</span>
          <span className="font-medium">
            {rxPct.toFixed(1)}% : {txPct.toFixed(1)}%
          </span>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden bg-gray-200">
          <div className="bg-green-500" style={{ width: `${rxPct}%` }} title={`Download: ${formatBytes(rxBytes)}`} />
          <div className="bg-orange-500" style={{ width: `${txPct}%` }} title={`Upload: ${formatBytes(txBytes)}`} />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            Download
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            Upload
          </span>
        </div>
      </div>
    </div>
  );
};
